import React, { useRef, useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import Button from "../ui/Button";
import TextInput from "../ui/TextInput";
import logo from "../../assets/images/logo-rewind.png";
import styles from "./DeleteModal.module.css";

interface DeleteModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    message: string;
    title?: string;
    requireTextConfirmation?: boolean;
    /** Info-only: single dismiss button, no delete action */
    infoOnly?: boolean;
    confirmLabel?: string;
}

const DeleteModal: React.FC<DeleteModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    message,
    title,
    requireTextConfirmation = false,
    infoOnly = false,
    confirmLabel,
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const modalContentRef = useRef<HTMLDivElement>(null);
    const [confirmationText, setConfirmationText] = useState("");

    const requiredText = String(t("delete")).toLowerCase();

    const isConfirmationValid = () => {
        if (!requireTextConfirmation) return true;
        return confirmationText.toLowerCase() === requiredText;
    };

    const handleOutsideClick = useCallback(
        (e: MouseEvent) => {
            if (modalContentRef.current && !modalContentRef.current.contains(e.target as Node)) {
                onClose();
            }
        },
        [onClose],
    );

    useEffect(() => {
        if (isOpen) {
            document.addEventListener("mousedown", handleOutsideClick);
        } else {
            // Reset confirmation text when modal closes
            setConfirmationText("");
        }
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [isOpen, handleOutsideClick]);

    if (!isOpen) return null;

    const modalTitle = title || t("confirm");

    const modal = (
        <div className={styles.overlay}>
            <div className={`${styles.content} delete-modal-content`} ref={modalContentRef}>
                <div className={styles.logoWrap}>
                    <img src={logo} alt="Logo" />
                </div>
                <h1 className={styles.title}>{modalTitle}</h1>

                <div className={styles.message}>{message}</div>

                {!infoOnly && requireTextConfirmation && (
                    <div className={styles.confirmInput}>
                        <p className={styles.confirmInstruction}>
                            {t("typeToConfirm")} <strong>"{requiredText}"</strong>
                        </p>
                        <TextInput
                            value={confirmationText}
                            onChange={(e) => setConfirmationText(e.target.value)}
                            placeholder={requiredText}
                            customClassName={styles.textInput}
                        />
                    </div>
                )}

                <div className={styles.actions}>
                    {infoOnly ? (
                        <Button variant="primary" shape="pill" label={confirmLabel || t("close")} onClick={onClose} />
                    ) : (
                        <>
                            <Button variant="cancel" shape="pill" label={t("cancel")} onClick={onClose} />
                            <Button
                                variant="danger"
                                shape="pill"
                                label={confirmLabel || t("delete")}
                                onClick={() => {
                                    onConfirm();
                                    onClose();
                                }}
                                disabled={!isConfirmationValid()}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(modal, document.body);
};

export default DeleteModal;
